import React from "react";
import { graphHelper, GraphPeriod, TITLE_CELL_WIDTH_PX } from "./graphUtils";
import { getMonthStart, toDateString } from '../../../utils/date';
import styled from "styled-components";
import { Link } from "react-router-dom";
import { TooltipContainerStyle } from "../../Toggletip";
import { colors } from "../../colors/color";

const EVENT_HEIGHT_PX = 24;
const DOT_DIM_PX = 16;

export type TimelineGraphEvent = {
    id: string,
    label: string,
    toolTip:React.ReactNode,
    linkUrl: string,
    start: Date,
    end: Date,
    color:string,
    isDot?: boolean
};

type TimelineGraphYearRowProps = {
    graphWidthPx: number,
    period: GraphPeriod,
    activities: TimelineGraphEvent[],
    firstRow?: boolean,
    eventGroupsTitle?: string,
};
export function TimelineGraphYearRow (props: TimelineGraphYearRowProps): JSX.Element
{
    // Ensuring day, hour, mins, .... are set right
    const adjustedProps = { ...props, yearMonthStart: getMonthStart(props.period.startMonthYear) };

    return <ActivityYearRowInternal {...adjustedProps} />;
}

export function ActivityYearRowInternal (props: TimelineGraphYearRowProps): JSX.Element
{
    type TimelineEvent = TimelineGraphEvent & 
    { 
        offsetPx: number, 
        widthPx: number; 
    };
    const subRows: { events: TimelineEvent[]; }[] = [{ events: [] }];

    const graphAreaWidthPx = Math.max(0, props.graphWidthPx - TITLE_CELL_WIDTH_PX);
    const graph = graphHelper(props.period, graphAreaWidthPx);
    for (let i = 0; i < props.activities.length; i++)
    {
        const activity = props.activities[i];

        const graphics = props.activities[i].isDot
            ? graph.getDotSpec({ start: props.activities[i].start, end: props.activities[i].end }, DOT_DIM_PX)
            : graph.getBarSpec({ start: props.activities[i].start, end: props.activities[i].end });
        
        if(graphics === null)
            continue;

        for(let rowIndex = 0; rowIndex < subRows.length; rowIndex++)
        {
            const row = subRows[rowIndex];
            if(!row.events.some(e => graph.detectGraphicsOverlap(e, graphics)))
            {
                subRows[rowIndex].events.push({ ...activity, ...graphics });
                break;
            }

            if(rowIndex === subRows.length - 1)
            {
                subRows.push({ events: [{ ...activity, ...graphics }] });
                break;
            }
        }
    }


    const rowHeight = EVENT_HEIGHT_PX * subRows.length + 2;


    const monthCells:JSX.Element[] = [];
    for (let i = 0; i < 12; i++)
    {
        monthCells.push(
            <MonthCell key={i} 
                firstRow={props.firstRow || false}
                style={{
                    position: 'absolute',
                    marginLeft: i * graphAreaWidthPx / 12,
                    height: rowHeight,
                    width: graphAreaWidthPx / 12
                }}
                data-timeline-graph="month-cell"
             />
        );
    }
    
    return (
        <Wrapper style={{ height:rowHeight}} data-timeline-graph={`wrapper-${props.eventGroupsTitle}`}>

            <TitleCell firstRow={props.firstRow || false} style={{ height: rowHeight }}>
                {props.eventGroupsTitle}
            </TitleCell>

            <GraphArea style={{ height: rowHeight }}>
                <div>
                    {monthCells}
                </div>

                {subRows.map((sr, sri) => {

                const subRowMarginTop = sri * EVENT_HEIGHT_PX + 1;

                return (
                    <React.Fragment key={sri}>
                        {sr.events.map((ev, evi) =>
                        {

                            return (
                                <EventBlockWrapper key={sri + '_' + evi}  
                                    style={{
                                        marginLeft: ev.offsetPx,
                                        marginTop: subRowMarginTop,
                                        width: ev.widthPx,
                                        height: EVENT_HEIGHT_PX,
                                        // border: '1px solid #F00',
                                        padding: 1
                                    }}
                                    data-timeline-graph={`event-block-${toDateString(ev.start)}-${toDateString(ev.end)}-offset_${ev.offsetPx}-width_${ev.widthPx}`}>

                                    <Link to={ev.linkUrl}>
                                        {ev.isDot
                                            ? <DotEventBlock color={ev.color} style={{
                                                    width:DOT_DIM_PX,
                                                    height:DOT_DIM_PX,
                                                    margin: `2px -2px`
                                                }}>
                                                    <EventToolTip
                                                        hexColor='#0f0'
                                                        data-timeline-graph="evet-tool-tip">

                                                        {ev.toolTip}

                                                    </EventToolTip>
                                                </DotEventBlock>
                                            : 
                                                <EventBlock
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                    }}
                                                    color={ev.color}>

                                                    <EventToolTip
                                                        hexColor='#0f0'
                                                        data-timeline-graph="evet-tool-tip">

                                                        {ev.toolTip}

                                                    </EventToolTip>

                                                </EventBlock>
                                        }
                                    </Link>
                                </EventBlockWrapper>
                            );

                        })}
                    </React.Fragment>
                );
            })}

            </GraphArea>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
`;

const TitleCell = styled.div<{ firstRow: boolean }>`
    width: ${TITLE_CELL_WIDTH_PX}px;
    flex-shrink: 0;
    padding: 2px 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: 'Noto Sans', sans-serif;
    font-size: 14px;
    color: ${colors.text.normal};
    border-left: 1px solid #EEE;
    /* border-right: 1px solid #EEE; */
    border-top: ${props => props.firstRow ? '1px solid #EEE' : '0px solid #EEE'};
    border-bottom: 1px solid #EEE;
`;

const GraphArea = styled.div`
    position: relative;
    flex: 1;
`;

const MonthCell = styled.div<{firstRow: boolean}>`
    border-left:1px solid #EEE;
    border-top:${props => props.firstRow ? '1px solid #EEE' : '0px solid #EEE'};
    border-bottom: 1px solid #EEE;
    &:last-child {
        border-right: 1px solid #EEE;
    }
`;

const EventBlockWrapper = styled.div`
    position: absolute;
`;

const EventToolTip = styled.div<{ hexColor: string; }>`
    ${TooltipContainerStyle}


    visibility: hidden;
    width: 250px;
    text-align: left;
    position: absolute;
    top: 25px;
    left: 50%;
    margin-left: -125px;
    opacity: 0;
    transition: opacity 0.3s;
`;

const EventBlock = styled.div<{ color: string; }>`
    position:relative;
    background-color:${props => props.color};
    border:1px solid ${props => props.color};
    border-radius: 4px;
    
    &:hover {
        border:1px solid ${colors.graphs.activityTimeline.hover};
        background-color: ${colors.graphs.activityTimeline.hover};
        & ${EventToolTip}{
            visibility: visible;   
            opacity: 1;
        }
    }
`;

const DotEventBlock = styled.div<{ color: string; }>`
    position:relative;
    background-color:${props => props.color};
    border:1px solid ${props => props.color};
    border-radius: 50%;
                                                    
    &:hover {
        border:1px solid ${colors.graphs.activityTimeline.hover};
        background-color: ${colors.graphs.activityTimeline.hover};
        & ${EventToolTip}{
            visibility: visible;   
            opacity: 1;
        }
    }
`;